import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import './styles/main.css';
import InstallPrompt from './components/InstallPrompt.jsx';

/**
 * Asistente Onboarding Bs./USD/EUR
 * - Wizard paso a paso con narrativa guiada
 * - Soporte para múltiples monedas base (USD, EUR)
 * - Escenario gris: vender USD para obtener Bs. vs usar Bs. existentes
 * - Tasas desde API con override manual + cache localStorage
 * - Cálculos con epsilon para empates + accesibilidad
 */

// --- APIs ---
const BCV_API_URL = 'https://ve.dolarapi.com/v1/dolares/oficial';
const PARALELO_API_URL = 'https://ve.dolarapi.com/v1/dolares/paralelo';

// --- Utilidades de formato ---
const formatUSD = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2
}).format(n);

const formatVES = (n) => new Intl.NumberFormat("es-VE", {
  style: "decimal", minimumFractionDigits: 4, maximumFractionDigits: 4
}).format(n) + " Bs.";

const formatCurrency = (amount, currency = 'USD') => {
  const symbols = { USD: '$', EUR: '€' };
  return `${symbols[currency] || '$'}${Number(amount).toFixed(2)}`;
};

const flagFor = (code) => {
  if (code === 'USD') return '/flags/us.svg';
  if (code === 'EUR') return '/flags/eu.svg';
  return '/flags/us.svg';
};
// --- Hook: manejo de tasas multi-moneda con cache ---
function useMultiCurrencyRates() {
  const [state, setState] = useState({
    usd: null, eur: null, // tasas oficiales X→Bs.
    paralelo: '', fecha: null, loading: true, error: null
  });

  const fetchWithRetry = useCallback(async (url, maxRetries = 3) => {
    for (let i = 0; i < maxRetries; i++) {
      try {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data && typeof data.promedio === "number") return data;
        throw new Error("Estructura inesperada");
      } catch (err) {
        if (i < maxRetries - 1) {
          await new Promise(r => setTimeout(r, 2 ** i * 1000));
        } else throw new Error(`Fallo al conectar con ${url}`);
      }
    }
  }, []);

  const reload = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const [usdData, parData] = await Promise.all([
        fetchWithRetry(BCV_API_URL),
        fetchWithRetry(PARALELO_API_URL)
      ]);

      const fecha = parData.fechaActualizacion || usdData.fechaActualizacion || new Date().toISOString();
      
      setState({
        usd: usdData.promedio,
        eur: 0, // Tasa manual - la tienda debe configurarla
        paralelo: parData.promedio.toFixed(4),
        fecha, loading: false, error: null
      });

      localStorage.setItem('rates', JSON.stringify({
        usd: usdData.promedio, eur: 0,
        paralelo: parData.promedio, fecha
      }));
    } catch (e) {
      setState(s => ({ ...s, loading: false, 
        error: "No se pudieron cargar las tasas. Puedes ingresarlas manualmente." }));
    }
  }, [fetchWithRetry]);

  useEffect(() => {
    const cached = localStorage.getItem('rates');
    if (cached) {
      const { usd, eur, paralelo, fecha } = JSON.parse(cached);
      setState(s => ({ ...s, usd, eur, 
        paralelo: Number(paralelo)?.toFixed?.(4) ?? String(paralelo),
        fecha, loading: false, error: null }));
    }
    reload();
  }, [reload]);

  return { ...state, setState, reload };
}

// --- Componentes UI ---
const Progress = ({ step, total }) => {
  const pct = step === 0 ? 0 : Math.round((step / (total - 1)) * 100);
  const label = step === 0 ? 'Introducción' : `Paso ${Math.min(step, 4)} de 4`;
  return (
    <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 p-4 shadow-sm z-50">
      <div className="max-w-3xl mx-auto">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-600">{label}</span>
          <span className="text-sm font-medium text-gray-800">{pct}%</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div className="h-3 bg-primary transition-all duration-300"
               style={{ width: `${pct}%` }} />
        </div>
      </div>
    </div>
  );
};

const StepCard = ({ icon, title, subtitle, children }) => (
  <div className="bg-white shadow-xl rounded-2xl p-6 md:p-8 border border-gray-100">
    <div className="flex items-start gap-4 mb-6">
      {icon && <div className="text-4xl">{icon}</div>}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-gray-600 mt-1 text-lg">{subtitle}</p>}
      </div>
    </div>
    <div className="space-y-6">
      {children}
    </div>
  </div>
);

const Pill = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-800 border border-blue-200",
    green: "bg-green-50 text-green-800 border border-green-200",
    pink: "bg-pink-50 text-pink-800 border border-pink-200",
    gray: "bg-gray-50 text-gray-800 border border-gray-200"
  };
  return <span className={`px-3 py-1 rounded-full text-sm font-medium ${tones[tone]}`}>{children}</span>;
};

const FooterNav = ({ canBack, canNext, onBack, onNext, nextLabel = "Siguiente" }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg z-40">
    <div className="max-w-2xl mx-auto flex gap-3 justify-between">
      <button type="button" onClick={onBack} disabled={!canBack}
        className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold border transition-all ${
          canBack ? "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm" 
                  : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
        }`}>
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Atrás
      </button>
      
      <button type="button" onClick={onNext} disabled={!canNext}
        className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white transition-all shadow-lg ${
          canNext ? "bg-primary hover:brightness-110 shadow-primary/30 transform hover:scale-105"
                  : "bg-gray-400 cursor-not-allowed shadow-gray-300/40"
        }`}>
        {nextLabel}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  </div>
);

// --- Modal de resultados ---
const ResultModal = ({ open, onClose, payload }) => {
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !payload) return null;

  const { recommendationText, differenceText, costCashDisplay, costBcvDisplay, 
    colorClass, recommendationAmount } = payload;

  return (
    <div id="modalOverlay" 
         className="fixed inset-0 bg-gray-900/70 flex items-center justify-center p-4 z-50"
         role="dialog" aria-modal="true" aria-labelledby="result-title"
         onClick={(e) => e.target.id === "modalOverlay" && onClose()}>
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl p-6 sm:p-8"
           onClick={(e) => e.stopPropagation()}>
        
        <h2 id="result-title" className="text-3xl font-bold text-gray-800 mb-6 text-center">
          ✅ Tu Mejor Opción
        </h2>

        <div className="space-y-6">
          <div className="text-center">
            <p className="font-semibold text-lg uppercase tracking-wide text-gray-600 mb-3">
              Recomendación:
            </p>
            <div className={`py-4 px-6 rounded-xl ${colorClass} text-center`}>
              <p className="text-2xl sm:text-3xl font-extrabold">{recommendationText}</p>
              {recommendationAmount && (
                <p className="text-lg font-bold mt-2">{recommendationAmount}</p>
              )}
            </div>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <p className="text-xl font-bold text-center text-gray-800">{differenceText}</p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-indigo-50 rounded-xl text-center border border-indigo-200">
              <p className="font-medium text-sm text-indigo-700">Costo Divisas:</p>
              <p className="text-xl font-bold text-indigo-900">{costCashDisplay}</p>
            </div>
            <div className="p-4 bg-teal-50 rounded-xl text-center border border-teal-200">
              <p className="font-medium text-sm text-teal-700">Costo BCV Real:</p>
              <p className="text-xl font-bold text-teal-900">{costBcvDisplay}</p>
            </div>
          </div>

          {/* Checklist eliminado a solicitud del usuario */}

          <button onClick={onClose} 
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-xl transition shadow-lg">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

// --- Cálculos principales ---
const calculateOptimalPayment = ({ 
  priceCurrency, priceAmount, cashPrice, bcvQuoteUSD,
  rateBcvSelected, rateParallel,
  rateUsdToVes // para conversiones cuando la moneda base no es USD
}) => {
  const EPS = 0.005; // medio centavo para empates
  
  // 1. Costo pagar en divisas (convertido a USD)
  let costCashUsd = cashPrice;
  if (priceCurrency !== 'USD' && rateUsdToVes) {
    // Convertir a USD: (EUR→Bs.) / (USD→Bs.) = EUR→USD, luego EUR * EUR→USD = USD
    const currencyToUsd = rateBcvSelected / rateUsdToVes;
    costCashUsd = cashPrice * currencyToUsd;
  }

  // 2. Costo pagar a BCV (trayendo a USD reales)
  // Usamos la tasa oficial que el usuario confirmó/edito en el paso anterior
  // para convertir la cotización a Bs., en lugar de leer directamente la API.
  const vesBcv = bcvQuoteUSD * rateBcvSelected; // (moneda base) * (BASE→Bs.) = Bs.
  // Usar siempre el costo de oportunidad (tasa paralela)
  const costBcvUsd = vesBcv / rateParallel;

  // 3. Comparación
  const diff = costBcvUsd - costCashUsd;
  const absDiff = Math.abs(diff);
  
  let recommendationText, differenceText, colorClass, recommendationAmount;
  
  if (absDiff < EPS) {
  recommendationText = "CUALQUIERA ES VALIDA";
  differenceText = "Ambas opciones cuestan practicamente lo mismo.";
    colorClass = "bg-blue-100 text-blue-700";
  } else if (diff < 0) {
  recommendationText = "PAGA A BCV (BOLIVARES)";
    differenceText = `Ahorro de ${formatUSD(absDiff)} pagando a BCV.`;
    colorClass = "bg-green-100 text-green-700";
    const vesFormatted = new Intl.NumberFormat('es-VE', { 
      minimumFractionDigits: 2, maximumFractionDigits: 2 
    }).format(vesBcv);
  recommendationAmount = `Paga: ${vesFormatted} Bs.`;
  } else {
    recommendationText = "PAGA EN DIVISAS (CASH)";
    differenceText = `Ahorro de ${formatUSD(absDiff)} pagando en divisas.`;
    colorClass = "bg-yellow-100 text-yellow-700";
    recommendationAmount = `Paga: ${formatCurrency(cashPrice, priceCurrency)}`;
  }

  return {
    recommendationText, differenceText, colorClass, recommendationAmount,
    costCashDisplay: formatUSD(costCashUsd),
    costBcvDisplay: formatUSD(costBcvUsd)
  };
};

// --- Componente Principal ---
const App = () => {
  const totalSteps = 5;
  const [step, setStep] = useState(0);

  // Factory para respuestas iniciales (evita referencias compartidas)
  const getInitialAnswers = useCallback(() => ({
    priceCurrency: 'USD',           // USD, EUR
    priceAmount: '',                // precio anunciado (vacío; usa placeholder)
    rateBcvSelected: '',            // tasa oficial seleccionada (editable)
    rateParallel: '',               // tasa paralela usuario
    cashPrice: '',                  // precio en divisas
    bcvQuoteUSD: ''                 // precio a BCV (en USD)
  }), []);
  
  // Tasas
  const { usd: rateUsd, eur: rateEur, paralelo, fecha, loading, error, reload } = 
    useMultiCurrencyRates();
  
  // Estado del wizard
  const [answers, setAnswers] = useState(getInitialAnswers());
  
  const [result, setResult] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rateBcvTouched, setRateBcvTouched] = useState(false);
  const [bcvError, setBcvError] = useState('');
  const [resettingBcv, setResettingBcv] = useState(false);
  // Estado para edición del precio BCV en Paso 3 (evita hooks dentro de Step3)
  const [editBcv, setEditBcv] = useState(false);
  const [tempBcv, setTempBcv] = useState('');
  // Ref para enfocar el primer input en Paso 1
  const priceAmountRef = useRef(null);

  // Reset de "tocado" al cambiar de moneda
  useEffect(() => {
    setRateBcvTouched(false);
  }, [answers.priceCurrency]);

  // Sincronizar tasa oficial según moneda: USD -> oficial USD (solo si no tocado), EUR -> 0 (solo si no tocado)
  useEffect(() => {
    if (rateBcvTouched) return;
    if (answers.priceCurrency === 'EUR') {
      if (answers.rateBcvSelected !== '0') {
        setAnswers(a => ({ ...a, rateBcvSelected: '0' }));
      }
      return;
    }
    if (answers.priceCurrency === 'USD' && rateUsd) {
      const desired = Number(rateUsd).toFixed(4);
      if (answers.rateBcvSelected !== desired) {
        setAnswers(a => ({ ...a, rateBcvSelected: desired }));
      }
    }
  }, [answers.priceCurrency, rateUsd, answers.rateBcvSelected, rateBcvTouched]);

  useEffect(() => {
    // Solo autollenar la tasa paralela cuando la moneda base es USD
    if (answers.priceCurrency === 'USD' && paralelo && !answers.rateParallel) {
      setAnswers(a => ({ ...a, rateParallel: paralelo }));
    }
  }, [paralelo, answers.priceCurrency, answers.rateParallel]);

  // Si se cambia a EUR y la tasa paralela actual es la de USD por defecto, limpiarla
  useEffect(() => {
    if (answers.priceCurrency === 'EUR' && answers.rateParallel === paralelo) {
      setAnswers(a => ({ ...a, rateParallel: '' }));
    }
  }, [answers.priceCurrency, answers.rateParallel, paralelo]);

  // Enfocar automáticamente el primer input al entrar al Paso 1
  useEffect(() => {
    if (step === 1 && priceAmountRef.current) {
      // pequeño timeout para asegurar que el nodo esté montado
      setTimeout(() => priceAmountRef.current?.focus?.(), 0);
    }
  }, [step]);

  // Al entrar al Paso 3, si aun no se ingreso "¿Y cuanto vale si pagas a BCV?",
  // autocompletarlo con el "Precio anunciado" para evitar reescribirlo.
  useEffect(() => {
    if (step === 3) {
      setAnswers(a => {
        const updates = {};
        if (!a.bcvQuoteUSD && a.priceAmount) updates.bcvQuoteUSD = a.priceAmount;
        return Object.keys(updates).length ? { ...a, ...updates } : a;
      });
    }
  }, [step]);

  // Validaciones por paso
  const validStep = (s) => {
    const p = Number(String(answers.rateParallel).replace(",", "."));
    const bcv = Number(String(answers.rateBcvSelected).replace(",", "."));
    const price = Number(String(answers.priceAmount).replace(",", "."));
    const cash = Number(String(answers.cashPrice).replace(",", "."));
    const quote = Number(String(answers.bcvQuoteUSD).replace(",", "."));
  // escenario gris removido: no hay 'sell'

    switch (s) {
      case 0: return true; // intro
      case 1: return !!answers.priceCurrency && price > 0 && bcv > 0; // moneda + precio + tasa
      case 2: return p > 0; // paralela
      case 3: return cash > 0 && quote > 0; // precios
      case 4: return true; // resultados
      default: return false;
    }
  };

  const resetWizard = useCallback(() => {
    setAnswers(getInitialAnswers());
    setResult(null);
    setModalOpen(false);
    setRateBcvTouched(false);
    setBcvError('');
    setResettingBcv(false);
    setStep(1); // volver directo al flujo de ingreso de datos
  }, [getInitialAnswers]);

  const handleNext = () => {
    // Si estamos en el último paso, iniciar un nuevo cálculo
    if (step === totalSteps - 1) {
      resetWizard();
      return;
    }
    // Calcular al pasar del paso de precios (3) a resultados (4)
    if (step === 3) {
      const calculation = calculateOptimalPayment({
        ...answers,
        priceAmount: Number(answers.priceAmount),
        cashPrice: Number(String(answers.cashPrice).replace(",", ".")),
        bcvQuoteUSD: Number(String(answers.bcvQuoteUSD).replace(",", ".")),
        rateBcvSelected: Number(String(answers.rateBcvSelected).replace(",", ".")),
        rateParallel: Number(String(answers.rateParallel).replace(",", ".")),
        rateUsdToVes: rateUsd
      });
      setResult(calculation);
      setModalOpen(true);
    }
    setStep(s => Math.min(s + 1, totalSteps - 1));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 0));

  // Handlers específicos para cada input para evitar re-renders
  const handlePriceAmountChange = useCallback((e) => {
    setAnswers(prev => ({ ...prev, priceAmount: e.target.value }));
  }, []);
  
  const handleRateBcvChange = useCallback((e) => {
    const v = e.target.value;
    setAnswers(prev => ({ ...prev, rateBcvSelected: v }));
    const num = Number(v);
  if (!v || isNaN(num) || num <= 0) setBcvError('Ingresa una tasa mayor a 0');
    else setBcvError('');
  }, []);

  const handleRateBcvFocus = useCallback(() => {
    if (!rateBcvTouched) {
      setRateBcvTouched(true);
      setAnswers(prev => ({ ...prev, rateBcvSelected: '0' }));
  setBcvError('Ingresa una tasa mayor a 0');
    }
  }, [rateBcvTouched]);

  const handleRateBcvBlur = useCallback(() => {
    const v = String(answers.rateBcvSelected);
    const num = Number(v);
  if (!v || isNaN(num) || num <= 0) setBcvError('Ingresa una tasa mayor a 0');
    else setBcvError('');
  }, [answers.rateBcvSelected]);
  
  const handleRateParallelChange = useCallback((e) => {
    setAnswers(prev => ({ ...prev, rateParallel: e.target.value }));
  }, []);
  
  const handleCashPriceChange = useCallback((e) => {
    setAnswers(prev => ({ ...prev, cashPrice: e.target.value }));
  }, []);
  
  const handleBcvQuoteChange = useCallback((e) => {
    setAnswers(prev => ({ ...prev, bcvQuoteUSD: e.target.value }));
  }, []);
  
  // (removido) tasa de venta: ya no se usa el escenario gris
  
  const handleSetParallelToBcv = useCallback(() => {
    if (rateUsd) {
      setAnswers(prev => ({ ...prev, rateParallel: Number(rateUsd).toFixed(4) }));
    }
  }, [rateUsd]);

  const handleResetBcvToOfficial = useCallback(() => {
    // Solo aplicable a USD; para EUR el flujo es manual (0)
    if (answers.priceCurrency === 'USD' && rateUsd) {
      setResettingBcv(true);
      setAnswers(prev => ({ ...prev, rateBcvSelected: Number(rateUsd).toFixed(4) }));
      setBcvError('');
      // Pequeña animación de carga para feedback visual
      setTimeout(() => setResettingBcv(false), 700);
    }
  }, [answers.priceCurrency, rateUsd]);
  
  const updateAnswer = useCallback((key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  }, []);

  // --- Pasos del wizard ---
  // Storytelling intro: Lottie + narrative + Comenzar button
  const Step0 = () => (
    <div className="bg-white shadow-xl rounded-2xl p-6 md:p-10 border border-gray-100 flex flex-col items-center">
      <div className="flex justify-center mb-6">
        <lottie-player
          src="/lottie/Confused.json"
          background="transparent"
          speed="1"
          style={{ width: 240, height: 240 }}
          loop
          autoplay
        ></lottie-player>
      </div>
  <h1 className="text-3xl md:text-4xl font-extrabold text-center text-purple-900 mb-4">¿Pagar en Divisas o Bolívares?</h1>
      <p className="text-lg md:text-xl text-gray-700 text-center mb-6 max-w-2xl">
        
        <br className="hidden md:block" />
        <span className="block mt-3">Aquí vas a saber que te conviene más.</span>
       
      </p>
      <div className="flex flex-col items-center gap-2 mt-2">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="px-8 py-4 bg-primary hover:brightness-110 text-white font-bold rounded-xl shadow-lg shadow-primary/20 transition text-xl flex items-center gap-3"
        >
          Comenzar
          <span className="bg-white text-primary text-xs font-semibold rounded-full px-3 py-1 shadow border border-primary/20">es gratis</span>
        </button>
      </div>
    </div>
  );
  const Step1 = () => {
    const rates = { USD: rateUsd, EUR: rateEur };
    const selectedRate = rates[answers.priceCurrency];

    return (
      <StepCard
        title="¿Cuánto cuesta tu producto?"
        subtitle={
          <>Indícanos el precio y la moneda para empezar.</>
        }
      >
        <div className="space-y-6">
          {/* Selector de moneda */}
          <div>
            <label className="block text-sm font-semibold mb-3 text-gray-700">
              {answers.priceAmount
                ? <>El precio "{answers.priceAmount}" que te dijeron es en:</>
                : <>El precio que te dijeron es en:</>}
            </label>
            <div className="flex justify-center">
              <div className="flex bg-gray-100 border border-gray-200 rounded-full p-1 gap-0 shadow-inner">
                {[
                  { code: 'USD', label: 'USD', flag: '/flags/us.svg' },
                  { code: 'EUR', label: 'EUR', flag: '/flags/eu.svg' }
                ].map(({ code, label, flag }, idx, arr) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => setAnswers(a => ({ ...a, priceCurrency: code }))}
                    className={`flex items-center gap-2 px-6 py-3 font-semibold text-lg transition focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm border-2 ${
                      answers.priceCurrency === code
                        ? 'border-primary bg-white text-primary z-10'
                        : 'border-transparent bg-gray-100 text-gray-700 hover:border-primary/40 z-0'
                    } ${idx === 0 ? 'rounded-l-full' : ''} ${idx === arr.length - 1 ? 'rounded-r-full' : ''}`}
                    style={{ minWidth: 120, marginLeft: idx > 0 ? '-1px' : 0 }}
                  >
                    {/* Use <img> to avoid inline SVG xlink namespace issues */}
                    <img src={flag} alt={`${label} flag`} className="w-7 h-7 rounded-full border border-gray-200 bg-white object-cover" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Input precio */}
          <div>
            <label htmlFor="priceAmount" className="block text-lg md:text-xl font-bold mb-3 text-gray-800">
              Escribe el precio anunciado del producto ({answers.priceCurrency}) BCV:
            </label>
            <div className="relative group z-0">
              {/* Glow overlay to emphasize primary input */}
              <div aria-hidden className="pointer-events-none absolute -inset-1 -z-10 rounded-2xl bg-primary/20 blur-md opacity-70 transition-all duration-300 group-focus-within:bg-primary/30 group-focus-within:blur-lg" />
              <input
                id="priceAmount"
                type="number"
                step="any"
                value={answers.priceAmount}
                onChange={handlePriceAmountChange}
                placeholder={`Ej: 5 ${answers.priceCurrency}`}
                autoFocus
                ref={priceAmountRef}
                className="w-full p-4 md:p-5 pr-24 border border-gray-300 rounded-xl bg-white text-gray-900 font-extrabold text-2xl md:text-3xl leading-tight focus:ring-4 focus:ring-primary/40 focus:border-primary"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg md:text-xl select-none">
                {answers.priceCurrency}/BCV
              </span>
            </div>
          </div>

          {/* Tasa oficial mostrada / editable */}
          {loading && (
            <div className="flex items-center gap-2 text-blue-600">
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/>
                <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Cargando tasas oficiales...
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <label htmlFor="rateBcvSelected" className="block text-xs font-medium mb-2 text-gray-600">
              Tasa oficial {answers.priceCurrency}→Bs. (confirma/edita):
            </label>
            <div className="relative">
              <input
                id="rateBcvSelected"
                type="number"
                step="any"
                value={answers.rateBcvSelected}
                onChange={handleRateBcvChange}
                onFocus={handleRateBcvFocus}
                onBlur={handleRateBcvBlur}
                placeholder={selectedRate ? selectedRate.toFixed(4) : "Ingresa la tasa"}
                className="w-full p-2.5 pr-24 border border-gray-300 rounded-lg bg-white text-gray-800 font-mono text-base font-normal focus:ring-2 focus:ring-primary/20 focus:border-primary/30"
              />
              <span className="absolute right-12 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium select-none">Bs.</span>
              <button
                type="button"
                onClick={handleResetBcvToOfficial}
                title="Restablecer a tasa oficial BCV"
                aria-label="Restablecer a tasa oficial BCV"
                className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md transition ${
                  answers.priceCurrency === 'USD' && rateUsd && !resettingBcv
                    ? 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/10'
                    : 'text-gray-400 cursor-not-allowed'
                }`}
                disabled={!(answers.priceCurrency === 'USD' && rateUsd) || resettingBcv}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={`w-5 h-5 ${resettingBcv ? 'animate-spin' : ''}`}>
                  <path fillRule="evenodd" d="M12 4a8 8 0 106.32 12.906 1 1 0 111.36 1.468A10 10 0 1112 2a1 1 0 110 2zm7-1a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 110-2h2.586l-1.293-1.293a1 1 0 111.414-1.414L19 6.586V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            {bcvError && (
              <p className="text-sm text-red-600 mt-2">{bcvError}</p>
            )}
            {answers.priceCurrency === 'EUR' && (
              <p className="text-sm text-gray-600 mt-2">
                Pregunta en el lugar cuál es la tasa EUR→Bs. o a cuánto te lo están dando y colócala aquí.
              </p>
            )}

            {fecha && answers.priceCurrency === 'USD' && (
              <p className="text-xs text-gray-400 mt-2">
                Ultima actualizacion: {new Date(fecha).toLocaleString('es-VE')}
              </p>
            )}
          </div>

          {error && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3" role="alert">
              <p className="text-yellow-800">{error}</p>
              <button onClick={reload} 
                      className="mt-2 px-3 py-1 bg-yellow-600 text-white rounded-lg text-sm hover:bg-yellow-700 transition">
                Reintentar carga
              </button>
            </div>
          )}
        </div>
      </StepCard>
    );
  };

  const Step2 = () => (
    <StepCard icon="" 
              title={answers.priceCurrency === 'EUR' ? '¿Cuántos Bs te pagan por 1 EUR ahora mismo?' : '¿Cuántos Bs te pagan por 1 USD ahora mismo?'} 
              subtitle={answers.priceCurrency === 'EUR' 
                ? 'Hay distintas formas de cambiar euros a bolívares. ¿Cuántos Bs te darían por 1 EUR?'
                : 'Hay distintas formas de cambiar dólares a bolívares. ¿Cuántos Bs te darían por 1 USD?'}>
      <div className="space-y-4">
        {/* Input va inmediatamente después del subtitle */}
        <div>
          <label htmlFor="rateParallel" className="block text-sm font-semibold mb-2 text-gray-700">
            {answers.priceCurrency === 'EUR' 
              ? '¿Cuántos Bs te pagan por 1 EUR ahora mismo?'
              : '¿Cuántos Bs te pagan por 1 USD ahora mismo?'}
          </label>
          <div className="relative flex items-center gap-3">
            <img src={flagFor(answers.priceCurrency)} alt={`${answers.priceCurrency} flag`} className="w-8 h-8 rounded-full border border-gray-200 bg-white object-cover" />
            <input
              id="rateParallel"
              type="number"
              step="any"
              placeholder={answers.priceCurrency === 'EUR' ? 'Ej: 350 Bs por 1 EUR' : 'Ej: 53.2000 Bs por 1 USD'}
              value={answers.rateParallel}
              onChange={handleRateParallelChange}
              className="w-full p-4 md:p-5 pr-24 border border-green-300 rounded-xl bg-white text-green-900 font-extrabold text-2xl md:text-3xl leading-tight font-mono focus:ring-4 focus:ring-primary/30 focus:border-primary"
            />
            <span className="absolute right-12 top-1/2 -translate-y-1/2 text-green-700 text-lg md:text-xl font-semibold select-none">Bs.</span>
            <button
              type="button"
              onClick={() => {
                // Solo para USD: pone la tasa paralela, no la oficial
                if (answers.priceCurrency === 'USD' && paralelo) {
                  setAnswers(prev => ({ ...prev, rateParallel: paralelo }));
                }
              }}
              title={answers.priceCurrency === 'USD' ? 'Actualizar con tasa paralela' : 'No disponible para EUR'}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                answers.priceCurrency === 'USD' && paralelo
                  ? 'text-green-700 hover:bg-green-100 hover:text-green-900'
                  : 'text-gray-400 cursor-not-allowed'
              }`}
              aria-label="Actualizar con tasa paralela"
              disabled={answers.priceCurrency !== 'USD' || !paralelo}
            >
              {/* Refresh icon */}
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                <path fillRule="evenodd" d="M12 4a8 8 0 106.32 12.906 1 1 0 111.36 1.468A10 10 0 1112 2a1 1 0 110 2zm7-1a1 1 0 011 1v5a1 1 0 01-1 1h-5a1 1 0 110-2h2.586l-1.293-1.293a1 1 0 111.414-1.414L19 6.586V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
        {/* Tip y otros bloques van después si se desean */}
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-green-800">
            <strong>Tip:</strong> {answers.priceCurrency === 'EUR' 
              ? 'Pregunta en casa cuánto pagan por 1 EUR en efectivo, Zelle o transferencia y escríbelo aquí.'
              : 'Pregunta en casa cuánto pagan por 1 USD en efectivo, Zelle o transferencia y escríbelo aquí.'}
          </p>
        </div>
      </div>
    </StepCard>
  );

  const Step3 = () => {
    const isUsingOfficialBcv = (answers.priceCurrency === 'USD' && !rateBcvTouched);
    const tasaLeyenda = isUsingOfficialBcv ? 'BCV' : 'la tasa que te está cobrando el local';

    return (
  <StepCard icon="" title="Dos precios, dos opciones" 
              subtitle="Si pago con billetes verdes cuesta una cosa, y si pago en bolívares es otra. ¡Ayúdame a poner los dos precios!">
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="bcvQuoteUSD" className="text-sm font-semibold text-orange-700">
                La tienda dijo que el producto cuesta en BCV:
              </label>
              <span
                className={`${isUsingOfficialBcv
                  ? 'bg-orange-100 text-orange-800 border border-orange-200'
                  : 'bg-orange-100 text-orange-800 border border-orange-200'} px-2 py-0.5 rounded-full text-xs font-semibold`}
                title={isUsingOfficialBcv ? 'Usando tasa oficial BCV' : 'Usando la tasa que te cobra el local'}
              >
                {isUsingOfficialBcv ? 'BCV' : 'Tasa local'}
              </span>
            </div>
            {/* Modo display / edición controlado desde App */}
            {!editBcv ? (
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-3">
                  <img src={flagFor(answers.priceCurrency)} alt={`${answers.priceCurrency} flag`} className="w-8 h-8 rounded-full border border-gray-200 bg-white object-cover" />
                  <span className="text-3xl font-extrabold text-orange-900 select-none">
                    {answers.bcvQuoteUSD ? formatCurrency(Number(answers.bcvQuoteUSD), answers.priceCurrency) : '--'}
                  </span>
                </span>
                <button
                  type="button"
                  className="ml-2 px-3 py-1 text-sm font-semibold rounded-lg border border-orange-300 bg-white text-orange-700 hover:bg-orange-100 transition"
                  onClick={() => {
                    setTempBcv(answers.bcvQuoteUSD || '');
                    setEditBcv(true);
                  }}
                >
                  Editar
                </button>
              </div>
            ) : (
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  setAnswers(prev => ({ ...prev, bcvQuoteUSD: tempBcv }));
                  setEditBcv(false);
                }}
              >
                <input
                  id="bcvQuoteUSD"
                  type="number"
                  step="any"
                  autoFocus
                  value={tempBcv}
                  onChange={(e) => setTempBcv(e.target.value)}
                  className="w-32 p-2 border border-orange-300 rounded-lg bg-white text-orange-800 font-bold text-lg focus:ring-2 focus:ring-orange-500"
                />
                <span className="text-base text-gray-400 select-none">{answers.priceCurrency}/BCV</span>
                <button
                  type="submit"
                  className="px-3 py-1 text-sm font-semibold rounded-lg border border-orange-300 bg-orange-600 text-white hover:bg-orange-700 transition"
                >
                  Guardar
                </button>
                <button
                  type="button"
                  className="px-3 py-1 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-100 transition"
                  onClick={() => {
                    setTempBcv(answers.bcvQuoteUSD || '');
                    setEditBcv(false);
                  }}
                >
                  Cancelar
                </button>
              </form>
            )}
            <p className="text-xs text-orange-700 mt-2">
              Este precio se convertirá a Bs. usando: <strong>{tasaLeyenda}</strong>.
            </p>
            <p className="text-[11px] text-orange-600 mt-1">
              Algunos negocios usan su propia tasa. Si ya lo sabes, cámbiala arriba.
            </p>
          </div>

            <div className="relative group z-0 bg-blue-50 border border-blue-200 rounded-2xl p-5 md:p-6 overflow-visible">
              {/* Glow overlay behind the card */}
              <div aria-hidden className="pointer-events-none absolute -inset-1 -z-10 rounded-3xl bg-indigo-400/40 blur-md transition-all duration-300 group-hover:blur-lg group-focus-within:blur-lg"></div>
              <label htmlFor="cashPrice" className="block text-lg md:text-xl font-bold mb-3 text-indigo-800">
                ¿Y qué pasa si pago con Divisas (Binance, Zelle o Efectivo)?
              </label>
            <div className="relative">
              <input
                id="cashPrice"
                type="number"
                step="any"
                placeholder="Ej: 25.00"
                value={answers.cashPrice}
                onChange={handleCashPriceChange}
                  className="w-full p-4 md:p-5 pr-20 border border-blue-300 rounded-xl bg-white text-blue-900 font-extrabold text-2xl md:text-3xl leading-tight focus:ring-2 focus:ring-blue-500"
              />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg md:text-xl select-none">
                {answers.priceCurrency}
              </span>
            </div>
          </div>
        </div>
      </div>
    </StepCard>
    );
  };

  const Step4 = () => (
    <StepCard icon="🎯" title="¿Qué me conviene?" 
              subtitle="¡Listo! He hecho las cuentas con tus datos.">
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
          <p className="text-green-800 mb-1 font-semibold">Tu mejor opción aparecerá en el cuadro.</p>
          <p className="text-sm text-green-700">Si quieres volver a empezar, usa “Hacer otro cálculo”.</p>
        </div>

        {/* Resumen de datos */}
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-gray-700 mb-3">📋 Resumen de tus datos:</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <span className="block text-blue-700 font-medium">Moneda base</span>
              <span className="flex items-center gap-2 font-mono font-bold text-blue-900">
                <img src={flagFor(answers.priceCurrency)} alt={`${answers.priceCurrency} flag`} className="w-5 h-5 rounded-full border border-gray-200 bg-white object-cover" />
                {answers.priceCurrency}
              </span>
            </div>
            <div className="p-3 bg-teal-50 rounded-lg border border-teal-200">
              <span className="block text-teal-700 font-medium">Tasa oficial</span>
              <span className="font-mono font-bold text-teal-900">
                {answers.rateBcvSelected ? formatVES(Number(answers.rateBcvSelected)) : '--'}
              </span>
            </div>
            <div className="p-3 bg-pink-50 rounded-lg border border-pink-200">
              <span className="block text-pink-700 font-medium">Tu paralela</span>
              <span className="font-mono font-bold text-pink-900">
                {answers.rateParallel ? formatVES(Number(answers.rateParallel)) : '--'}
              </span>
            </div>
            <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-200">
              <span className="block text-indigo-700 font-medium">Precio divisas</span>
              <span className="font-mono font-bold text-indigo-900">
                {answers.cashPrice ? formatCurrency(Number(answers.cashPrice), answers.priceCurrency) : '--'}
              </span>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <span className="block text-yellow-700 font-medium">Precio BCV (USD)</span>
              <span className="font-mono font-bold text-yellow-900">
                {answers.bcvQuoteUSD ? formatUSD(Number(answers.bcvQuoteUSD)) : '--'}
              </span>
            </div>
          </div>
        </div>

        {/* Checklist eliminado a solicitud del usuario */}
      </div>
    </StepCard>
  );

  // --- Render principal ---
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Progress step={step} total={totalSteps} />
      <InstallPrompt />
      <div className="flex-1 flex flex-col justify-center items-center w-full">
        <div className="w-full max-w-3xl flex justify-center items-center">
          <main className="w-full">
            {/* Renderizamos el contenido directamente para evitar remount y pérdida de foco */}
            {step === 0 && Step0()}
            {step === 1 && Step1()}
            {step === 2 && Step2()}
            {step === 3 && Step3()}
            {step === 4 && Step4()}
          </main>
        </div>
        {step > 0 && (
          <>
            <FooterNav
              canBack={step > 0}
              canNext={validStep(step)}
              onBack={handleBack}
              onNext={handleNext}
              nextLabel={step === totalSteps - 1 ? "Hacer otro cálculo" : (step === 3 ? "Comparar" : "Siguiente")}
            />
            <footer className="text-center mt-8">
              <p className="text-xs text-gray-500">
                💡 El cálculo usa tu tasa como costo de oportunidad para convertir Bs. a USD reales.
              </p>
            </footer>
          </>
        )}
      </div>
      <ResultModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        payload={result}
      />
      {result?.error && (
        <div className="fixed bottom-4 left-4 right-4 mx-auto max-w-lg bg-red-100 text-red-800 border border-red-200 p-4 rounded-xl shadow-lg"
             role="alert" aria-live="assertive">
          <div className="flex items-center justify-between gap-3">
            <p className="font-semibold">{result.error}</p>
            <button onClick={() => setResult(null)}
                    className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 transition">
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;